import { useMemo, useState } from "react";
import { Box } from "@mui/material";

import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { DataTable } from "components/DataTable/DataTable";
import { useFormat } from "hooks/useFormat";
import { ILog } from "interfaces/logs.interface";
import { IZone } from "interfaces/zone.interface";

import { NotificationLogDetailsDialog } from "./dialogs/NotificationLogDetailsDialog";

const getResource = (log: ILog) => {
  if (log.rule?.entity_type === "subject") {
    return log?.subject_identifier ?? "-";
  }

  if (log.rule?.entity_type === "device") {
    return log.device_serial_number ?? "-";
  }

  return "-";
};

type LogTableEntry = ILog & {
  timestamp: string;
  currentState: string;
  ruleType: string;
};

type NotificationLogListProps = {
  logs: ILog[];
  zoneEntity: Record<string, IZone>;
};

export function NotificationLogList({
  logs,
  zoneEntity = {},
}: NotificationLogListProps) {
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState<string | null>(null);

  const { format } = useFormat();

  const columns = useMemo(
    (): ColumnDef<LogTableEntry>[] => [
      {
        id: "timestamp",
        accessorKey: "timestamp",
        header: t("timestamp"),
      },
      {
        id: "zone",
        accessorKey: "zone_id",
        header: t("Zone"),
        cell: ({ row }) => zoneEntity[row.getValue<string>("zone")]?.name,
      },
      {
        accessorKey: "subject_identifier",
        header: t("subject_identifier"),
        cell: ({ row }) => getResource(row.original),
      },
      {
        id: "ruleType",
        accessorKey: "ruleType",
        header: t("type"),
      },
      {
        id: "currentState",
        accessorKey: "currentState",
        header: t("Current state"),
      },
      {
        id: "details",
        accessorKey: "details",
        header: t("details"),
      },
    ],
    [t, zoneEntity],
  );

  const data = logs.map((log) => ({
    ...log,
    timestamp: log.rule?.current_state?.timestamp
      ? format(new Date(log.rule?.current_state?.timestamp), "PPPpp")
      : "-",
    ruleType: log.rule?.type ? t(log.rule.type) : "-",
    currentState: log.rule?.current_state?.state
      ? t(log.rule.current_state.state)
      : "-",
    details: log.rule?.current_state?.detail ?? "-",
  }));

  const logToShow = data.find((l) => l.id === showDialog);

  return (
    <Box>
      <DataTable
        tableId={"notification-logs"}
        columns={columns}
        data={data}
        enableSorting={false}
        getRowId={(row) => row.id}
        onRowClick={(row) => setShowDialog(row.id)}
      />
      <NotificationLogDetailsDialog
        isOpen={!!logToShow}
        onClose={() => setShowDialog(null)}
        notificationLog={logToShow}
        zone={zoneEntity[logToShow?.zone_id || ""]?.name || ""}
      />
    </Box>
  );
}
